import { ApiError } from '@teko/api';
import { RELOAD_DUE_TO_MISSING_ACCESS_TOKEN, useAuth } from '@teko/auth-react';
import {
  AddItemsProduct,
  AddItemsRequest,
  AllowedUserActionItem,
  Cart,
  CartItem,
  IsolatedCartType,
  defaultCart,
  getCartToken,
  removeCartToken,
  setCartToken,
} from '@teko/cart/v2';
import {
  Modal,
  SubTitle,
  useToast as useDesktopToast,
  useLoading,
  useModal,
} from '@teko/ds-desktop';
import { Shape, useToast as useMobileToast } from '@teko/ds-mobile';
import { logger } from '@teko/log';
import terraKit from '@terra-js/terra-kit';
import { useFeatureFlags, useScreen, useServices } from 'contexts';
import CryptoJS from 'crypto-js';
import DOMPurify from 'dompurify';
import { formatPhoneNumberPrefix } from 'helpers';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useRouter } from 'next/router';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApplyPromotionParams,
  ApplyVoucherParams,
  PriceEditorProps,
} from 'types';

export enum cartErrorCode {
  APPLY_EXCLUSION_PROMOTION = 409001,
  ADD_PRODUCT_WITH_EXCLUSION_PROMOTION = 409002,
  EXPIRE_ACCESS_TOKEN = 401001,
}

export interface CartContextStateV2 {
  // loading state for API call
  loading: boolean;
  // cart data returned from API
  cart: Cart;
  // all item in cart (flatten)
  flattenItems: CartItem[];

  // functions for components to use
  getCart: (params: {
    withLoading: boolean;
    removeToken?: boolean;
  }) => Promise<void>;
  onAddSingleItem: (
    body: AddItemsProduct,
    fromProductCard?: boolean,
    isolatedCartType?: IsolatedCartType,
  ) => Promise<Cart | null>;
  onAddMultipleItems: (
    body: AddItemsProduct[],
    options?: Pick<AddItemsRequest, 'isolatedCartType' | 'isSkipHoldInventory'>,
  ) => Promise<void>;
  onAddComboItems: (
    body: AddItemsRequest,
    fromProductCard?: boolean,
  ) => Promise<void>;
  onRemoveItem: (item: CartItem) => Promise<void>;
  onIncrease: (item: CartItem) => Promise<void>;
  onDecrease: (item: CartItem) => Promise<void>;
  onChangeQuantity: (
    item: CartItem,
    newQuantity: number,
    showLoading?: boolean,
  ) => Promise<void>;
  onUpdateItemNote: (item: CartItem, note: string) => Promise<void>;
  onSelectItem: (item: CartItem) => Promise<void>;
  onSelectSeller: (sellerItems: CartItem[], selected: boolean) => Promise<void>;
  onDeleteCart: () => Promise<void>;
  isEmpty?: boolean;
  onChangeItemPrice: (value: PriceEditorProps) => Promise<void>;
  onApplyPromotion: (
    applyPromotionParams: ApplyPromotionParams,
    isBuyNow?: boolean,
    isForceApply?: boolean,
  ) => Promise<Cart | undefined>;
  onRemovePromotion: (
    cartPromotionId: string,
    isBuyNow?: boolean,
  ) => Promise<Cart | undefined>;
  onApplyVoucher: (
    applyVoucherParams: ApplyVoucherParams,
    isBuyNow?: boolean,
    isForceApply?: boolean,
  ) => Promise<Cart | undefined>;
  onRequestItemSku: (name: string, quantity?: number) => Promise<void>;
  totalCartItems: number;
  onApplyPromotionInCartPage: (
    applyPromotionParams: ApplyPromotionParams,
    isBuyNow?: boolean,
  ) => Promise<Cart | undefined>;
  onApplyVoucherInCartPage: (
    applyVoucherParams: ApplyVoucherParams,
    isBuyNow?: boolean,
    isForceApply?: boolean,
  ) => Promise<Cart | undefined>;
  isUpdatingItemQuantity: { [key: string]: boolean };
}

const defaultCartContext: CartContextStateV2 = {
  loading: false,
  cart: defaultCart,
  flattenItems: [],
  getCart: () => Promise.resolve(),
  onAddSingleItem: () => Promise.resolve(defaultCart),
  onAddComboItems: () => Promise.resolve(),
  onAddMultipleItems: () => Promise.resolve(),
  onRemoveItem: () => Promise.resolve(),
  onIncrease: () => Promise.resolve(),
  onDecrease: () => Promise.resolve(),
  onChangeQuantity: () => Promise.resolve(),
  onUpdateItemNote: () => Promise.resolve(),
  onSelectItem: () => Promise.resolve(),
  onSelectSeller: () => Promise.resolve(),
  onDeleteCart: () => Promise.resolve(),
  onChangeItemPrice: () => Promise.resolve(),
  isEmpty: true,
  onApplyPromotion: () => Promise.resolve(undefined),
  onRemovePromotion: () => Promise.resolve(undefined),
  onApplyVoucher: () => Promise.resolve(undefined),
  onRequestItemSku: () => Promise.resolve(),
  totalCartItems: 0,
  onApplyPromotionInCartPage: () => Promise.resolve(undefined),
  onApplyVoucherInCartPage: () => Promise.resolve(undefined),
  isUpdatingItemQuantity: {},
};

const CartContext = React.createContext<CartContextStateV2 | undefined>(
  defaultCartContext,
);

export interface CartProviderPropsV2 {
  children: React.ReactNode;
}

export const CartProviderV2: ({
  children,
}: CartProviderPropsV2) => JSX.Element = ({
  children,
}: CartProviderPropsV2) => {
  const desktopToast = useDesktopToast();
  const mobileToast = useMobileToast();
  const router = useRouter();
  const { query } = router;
  const [loading, setLoading] = useLoading('CartContextV2');
  const { showModal } = useModal();
  const [cart, setCart] = useState<Cart>(defaultCart);
  const tokenPromise = useRef<Promise<void> | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isUpdatingItemQuantity, setIsUpdatingItemQuantity] = useState<{
    [key: string]: boolean;
  }>({});

  const { cartToken: queryCartToken } = query;

  const { user, loading: authLoading } = useAuth();

  const {
    terminal,
    isMobile,
    getProcessEnv,
    partner,
    iamClientId,
    requireLogin,
    pushTrackingForAdvanceTransform,
  } = useTenantConfig();

  const { cartServiceV2, authService } = useServices();

  const { trackingScreenName } = useScreen();

  const { flags } = useFeatureFlags();

  const showErrorToast = (message: string) => {
    if (isMobile) {
      mobileToast.error?.({
        message,
        customStyle: { zIndex: 2050 },
      });
    } else {
      desktopToast.error?.({ title: message, message: null });
    }
  };

  // common handler for handling Error in most Cart functions
  const handleError = (
    error: unknown,
    suppressThrowError?: boolean,
    suppressShowingErrorToast?: boolean,
    onOk?: () => void,
  ): void => {
    logger.error(error);
    const errorCode =
      (error as ApiError).code === (error as ApiError).response.code
        ? (error as ApiError).code
        : (error as ApiError).response.code;
    const {
      EXPIRE_ACCESS_TOKEN,
      APPLY_EXCLUSION_PROMOTION,
      ADD_PRODUCT_WITH_EXCLUSION_PROMOTION,
    } = cartErrorCode;

    switch (errorCode) {
      case EXPIRE_ACCESS_TOKEN: // case missing access token
        if (
          sessionStorage.getItem(RELOAD_DUE_TO_MISSING_ACCESS_TOKEN) !== '1'
        ) {
          setErrorMessage((error as ApiError).message);
        }
        break;
      case APPLY_EXCLUSION_PROMOTION:
      case ADD_PRODUCT_WITH_EXCLUSION_PROMOTION:
        showModal?.({
          title: t('Thông báo'),
          children: (
            <Shape
              dangerouslySetInnerHTML={{
                __html: DOMPurify?.sanitize?.(
                  `${(error as ApiError).message}\n${t(
                    'Bạn có chắc chắn muốn thay đổi khuyến mại?',
                  )}`,
                ),
              }}
            />
          ),
          okText: t('Xác nhận'),
          cancelText: t('Quay lại'),
          onOk: () => {
            onOk?.();
          },
          zIndex: 1051,
        });
        break;
      default:
        if (!suppressShowingErrorToast) {
          showErrorToast((error as Error).message);
        }
        if (!suppressThrowError) throw error;
    }
  };

  const showSuccessToast = (message: string) => {
    if (isMobile) {
      mobileToast.success?.({
        message,
        customStyle: { zIndex: 2050 },
      });
    } else {
      desktopToast.success?.({ title: message, message: null });
    }
  };

  // getCart when login success
  useEffect(() => {
    const fetchCart = async () => {
      const cartToken =
        terraKit.isAvailable() && flags.epic_ecom_479
          ? // use native app cart token. Ref: https://jira.teko.vn/browse/UX4BUYER-5792
            await terraKit.cart().getCartToken()
          : (queryCartToken as string);

      if (!!cartToken) {
        setCartToken(cartToken);
      }

      // loadingIAMState somehow doesn't work here
      // use localStorage instead
      const clientId =
        iamClientId || getProcessEnv('NEXT_PUBLIC_IAM_CLIENT_ID');
      const isUserInfoInLS = localStorage.getItem(`tekoid.user.${clientId}`);

      if (authLoading) return;

      if (terraKit.isAvailable() && flags.epic_ecom_479) {
        // alway get cart in embeded webview
        getCart({
          withLoading: false,
          removeToken: false,
        });
      } else if (
        user?.id ||
        //user is not logged with partner
        (!isUserInfoInLS && !partner && !terraKit.isAvailable())
      ) {
        getCart({
          withLoading: router.pathname.includes('/cart'),
          removeToken: false,
        });
      }
    };

    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, queryCartToken, authLoading, terraKit.isAvailable()]);

  const flattenItems = useMemo<CartItem[]>(
    () =>
      cart.items.reduce(
        (prev, curr) => [...prev, ...curr.items],
        [] as CartItem[],
      ),
    [cart.items],
  );

  // check for cartToken, if cartToken is empty then create new one.
  const validateCartToken = async () => {
    if (!getCartToken() && !tokenPromise.current) {
      tokenPromise.current = createCart().finally(
        () => (tokenPromise.current = null),
      );
    }
    await tokenPromise.current;
  };

  // create new cart from cart service v2
  const createCart = async () => {
    try {
      const { cartToken } = await cartServiceV2.createNewCart({});
      if (cartToken) {
        setCartToken(cartToken);
      }
    } catch (error) {
      handleError(error);
    }
  };

  // get Cart from Cart Service V2
  const getCart = async ({
    withLoading = false,
    removeToken = true,
  }): Promise<void> => {
    try {
      if (!terminal) return;
      if (withLoading) setLoading(true);
      await validateCartToken();
      const cart = await cartServiceV2.getCart();
      setCart(cart);
      if (removeToken && cart?.items?.length === 0) removeCartToken();
    } catch (error) {
      if (!withLoading) {
        // without explicit loading,
        // do not show the error toast
        // since it cause confusion to users
        // in page such as condition
        handleError(error, false, true);
      } else {
        handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateNativeAppCart = async (cartResponse: Cart) => {
    try {
      if (terraKit.isAvailable() && flags.epic_ecom_479 && cartResponse) {
        await terraKit.cart().updateCartSuccess(JSON.stringify(cartResponse));
      }
    } catch (e) {
      logger.error(e);
    }
  };

  const onAddToCart = async (
    body: AddItemsRequest,
    fromProductCard = false,
  ): Promise<Cart | null> => {
    // force login when add to card with tenant require login
    if (requireLogin && !user) {
      if (!authLoading) {
        authService.login();
      }
      return null;
    }

    const {
      groups,
      override,
      isolatedCartType,
      overrideItemId,
      isSkipHoldInventory,
    } = body;

    try {
      setLoading(true);

      const { trackAddToCartManually, trackPVFormAddToCart } = await import(
        '@teko/tracking'
      );
      if (fromProductCard && trackingScreenName) {
        setTimeout(() => {
          groups.forEach((group) => {
            group.products.forEach((product) => {
              trackAddToCartManually(
                trackingScreenName,
                product.sellerSku || '',
              );
            });
          });
        }, 0);
      }
      await validateCartToken();
      const res = await cartServiceV2.addItems({
        groups,
        override,
        overrideItemId,
        isolatedCartType,
        isSkipHoldInventory,
      });
      if (!!user && pushTrackingForAdvanceTransform) {
        const emailSha256 = !!user.email
          ? CryptoJS.SHA256(user.email).toString()
          : undefined;
        const phoneSha256 = !!user.telephone
          ? CryptoJS.SHA256(formatPhoneNumberPrefix(user.telephone)).toString()
          : undefined;
        trackPVFormAddToCart(res.grandTotalAmount, emailSha256, phoneSha256);
      }
      showSuccessToast(t('Đã thêm sản phẩm vào giỏ hàng'));
      if (!isolatedCartType) {
        setCart(res);
      }
      // in case add hamper to cart (isolatedCartType = IsolatedCartType.BUY_NOW && isSkipHoldInventory = true) -> need to skip updateNativeAppCart (event updateCartSuccess for app)
      if (
        isolatedCartType !== IsolatedCartType.BUY_NOW ||
        !isSkipHoldInventory
      ) {
        updateNativeAppCart(res);
      }
      return res;
    } catch (error) {
      handleError(error, false, false, () =>
        onAddToCart({ ...body, override: true }, fromProductCard),
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  // handle user add an item to cart
  const onAddSingleItem = async (
    { sku, sellerSku, quantity, promotionId, isPrimary }: AddItemsProduct,
    fromProductCard = false,
    isolatedCartType?: IsolatedCartType,
  ): Promise<Cart | null> => {
    const body = {
      groups: [
        { products: [{ sku, sellerSku, quantity, promotionId, isPrimary }] },
      ],
      override: false,
      isolatedCartType: isolatedCartType,
    };
    return await onAddToCart(body, fromProductCard);
  };

  // handle user add multiple item to cart
  const onAddMultipleItems = async (
    productList: AddItemsProduct[],
    options?: Pick<AddItemsRequest, 'isolatedCartType' | 'isSkipHoldInventory'>,
  ): Promise<void> => {
    const body = {
      groups: [{ products: productList }],
      override: false,
      ...options,
    };
    await onAddToCart(body, false);
  };

  // handle user add combo to cart
  const onAddComboItems = async (body: AddItemsRequest): Promise<void> => {
    await onAddToCart(body, false);
  };

  // handle when user remove an item
  const onRemoveItem = async (item: CartItem): Promise<void> => {
    const { trackRemoveFromCart } = await import('@teko/tracking');
    try {
      trackRemoveFromCart(item, cart.id);
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.removeItem({
        itemId: item.id,
      });
      setCart(res);
      updateNativeAppCart(res);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // handle when user click add button on an item
  const onIncrease = async (item: CartItem): Promise<void> => {
    return onChangeQuantity(item, item.quantity + 1);
  };

  // handle when user click subtract button on an item
  const onDecrease = async (item: CartItem): Promise<void> => {
    return onChangeQuantity(item, item.quantity - 1);
  };

  // handle when user change quantity of an item
  const onChangeQuantity = async (
    item: CartItem,
    newQuantity: number,
    showLoading: boolean = true,
  ): Promise<void> => {
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setIsUpdatingItemQuantity({ [item.id]: true });
      }
      await validateCartToken();
      const { trackChangeQuantityProduct } = await import('@teko/tracking');
      trackChangeQuantityProduct(item, newQuantity, cart.id);
      const res = await cartServiceV2.updateItemQuantity({
        itemId: item.id,
        quantity: newQuantity,
      });
      setCart(res);
      updateNativeAppCart(res);
    } catch (error) {
      handleError(error);
    } finally {
      if (showLoading) {
        setLoading(false);
      } else {
        setIsUpdatingItemQuantity({ [item.id]: false });
      }
    }
  };

  const onUpdateItemNote = async (
    item: CartItem,
    note: string,
  ): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.updateItemNote({
        itemId: item.id,
        note,
      });
      setCart(res);
      updateNativeAppCart(res);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const onSelectItems = async (
    itemIdList: string[],
    isSelected: boolean,
  ): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.selectItems({
        isSelected,
        itemIds: itemIdList,
      });
      setCart(res);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // handle when user click checkbox of an item
  const onSelectItem = async (item: CartItem): Promise<void> => {
    const { trackSelectProduct } = await import('@teko/tracking');
    trackSelectProduct(item, !item.isSelected, cart.id);
    return onSelectItems([item.id], !item.isSelected);
  };

  // handle when user click on Checkbox of a seller
  const onSelectSeller = async (
    sellerItems: CartItem[],
    selected: boolean,
  ): Promise<void> => {
    // only toggle items which its allowedUserActions contains TOGGLE value
    const canToggleCartItems = (sellerItems || []).filter((item) =>
      (item.allowedUserActions || []).includes(AllowedUserActionItem.TOGGLE),
    );
    return onSelectItems(
      canToggleCartItems.map((item) => item.id),
      selected,
    );
  };

  // handle when user click delete all cart
  const onDeleteCart = async (): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      await cartServiceV2.deleteCart();
      await getCart({ withLoading: true });
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const onChangeItemPrice = async (
    priceEditorData: PriceEditorProps,
  ): Promise<void> => {
    try {
      setLoading(true);
      await validateCartToken();
      const { id, editedPrice, price } = priceEditorData;
      const newCart = await cartServiceV2.changeItemPrice({
        itemId: id,
        consultationFee: Number(editedPrice) - price,
      });

      if (newCart) {
        setCart(newCart);
        showSuccessToast(t('Sửa giá sản phẩm thành công'));
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // function to handle apply promotion
  const onApplyPromotion = async (
    applyPromotionParams: ApplyPromotionParams,
    isBuyNow?: boolean,
    isForceApply?: boolean,
  ) => {
    const { productId, promotionCode, promotionId, promotionSource } =
      applyPromotionParams;
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.applyPromotion({
        ...(!!productId && { itemId: productId }),
        ppmPromotionId: promotionId,
        ppmCode: promotionCode,
        isForceApply,
        promotionSource,
      });

      showSuccessToast(t('Áp dụng khuyến mãi thành công'));

      // Keep original cart data since buyNow flow use a separate cart.
      if (!isBuyNow) {
        setCart(res.data);
      }
      return res.data;
    } catch (error) {
      logger.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onApplyPromotionInCartPage = async (
    applyPromotionParams: ApplyPromotionParams,
    isBuyNow?: boolean,
    isForceApply?: boolean,
  ) => {
    try {
      const res = await onApplyPromotion(
        applyPromotionParams,
        isBuyNow,
        isForceApply,
      );
      return res;
    } catch (error) {
      handleError(error, false, false, () =>
        onApplyPromotionInCartPage(applyPromotionParams, isBuyNow, true),
      );
    }
  };

  // function to handle remove promotion
  const onRemovePromotion = async (
    cartPromotionId: string,
    isBuyNow?: boolean,
  ) => {
    try {
      setLoading(true);
      await validateCartToken();
      const req = {
        cartPromotionId,
      };

      const res = await cartServiceV2.removePromotion(req);
      showSuccessToast(t('Bỏ áp dụng khuyến mãi thành công'));
      // Keep original cart data since buyNow flow use a separate cart.
      if (!isBuyNow) {
        setCart(res.data);
      }
      return res.data;
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const onApplyVoucher = async (
    applyVoucherParams: ApplyVoucherParams,
    isBuyNow?: boolean,
    isForceApply?: boolean,
  ) => {
    try {
      setLoading(true);
      await validateCartToken();
      const { ppmCode, promotionSource } = applyVoucherParams;
      const res = await cartServiceV2.applyPromotion({
        ppmCode,
        isForceApply,
        promotionSource,
      });

      showSuccessToast(t('Áp dụng mã giảm giá/phiếu mua hàng thành công'));
      // Keep original cart data since buyNow flow use a separate cart.
      if (!isBuyNow) {
        setCart(res.data);
      }
      return res.data;
    } catch (error) {
      logger.error(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const onApplyVoucherInCartPage = async (
    applyVoucherParams: ApplyVoucherParams,
    isBuyNow?: boolean,
    isForceApply?: boolean,
  ) => {
    try {
      const res = await onApplyVoucher(
        applyVoucherParams,
        isBuyNow,
        isForceApply,
      );
      return res;
    } catch (error) {
      handleError(error, false, false, () =>
        onApplyVoucherInCartPage(applyVoucherParams, isBuyNow, true),
      );
    }
  };

  const onRequestItemSku = async (
    name: string,
    quantity = 1,
  ): Promise<void> => {
    // force login when add to card with tenant require login
    if (requireLogin && !user) {
      if (!authLoading) {
        authService.login();
      }
      return;
    }
    try {
      setLoading(true);
      await validateCartToken();
      const res = await cartServiceV2.requestItemSku({
        name,
        quantity,
      });
      showSuccessToast(t('Đã thêm sản phẩm vào giỏ hàng'));
      setCart(res);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        loading,
        cart,
        flattenItems,
        getCart,
        onAddSingleItem,
        onAddMultipleItems,
        onAddComboItems,
        onRemoveItem,
        onIncrease,
        onDecrease,
        onChangeQuantity,
        onUpdateItemNote,
        onSelectItem,
        onSelectSeller,
        onDeleteCart,
        get isEmpty() {
          return cart?.items?.length === 0;
        },
        onChangeItemPrice,
        onApplyPromotion,
        onRemovePromotion,
        onApplyVoucher,
        onRequestItemSku,
        get totalCartItems() {
          return (flattenItems || []).length;
        },
        onApplyPromotionInCartPage,
        onApplyVoucherInCartPage,
        isUpdatingItemQuantity,
      }}
    >
      {children}
      <Modal
        visible={!!errorMessage}
        title={t('Thông báo')}
        okText={t('Tải lại trang')}
        onOk={() => {
          window.location.reload();
          sessionStorage.setItem(RELOAD_DUE_TO_MISSING_ACCESS_TOKEN, '1');
        }}
        zIndex={2000}
        showCloseIcon={false}
        alignFooter="center"
        cancelText=""
      >
        <SubTitle level={1} weight="regular">
          {errorMessage}
        </SubTitle>
      </Modal>
    </CartContext.Provider>
  );
};

export function useCartV2(): CartContextStateV2 {
  const context = useContext<CartContextStateV2 | undefined>(CartContext);
  if (!context) {
    throw new Error(
      'useCartV2 must be inside a CartProviderV2 with a state value',
    );
  }
  return context;
}
